import { Icon, type IconGlyph } from '@sunwebgroup/uds-ui/icon'
import { mqMax, mqMin, tok, tokPx } from '@sunwebgroup/ui-components'
import {
  LazyImage,
  TabBar,
  Tag,
  Typography,
} from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'
import { buildImgSrc, buildImgSrcSet } from '@/helpers/image-url'
import { getImageOrigin } from '@/scopes/account/common/utils/getImageOrigin'

export type Tabs = 'booking' | 'participants' | 'rooms' | 'bus'
const HERO_IMAGE_SRCSET_WIDTHS = [640, 960, 1280, 1600, 1920, 2400]
const HERO_IMAGE_PLACEHOLDER_WIDTH = 240
const HERO_IMAGE_SIZES = '(min-width: 1600px) 1600px, 100vw'

interface TabComponent {
  value: Tabs
  icon: IconGlyph
  label: string
}

const HeroContainer = styled.div`
  position: relative;
  margin: 0 auto;
  width: 100%;
  max-width: 1600px;
  padding: ${tokPx('primitives_space_48')};

  @media ${mqMax('primitives_breakpoint_s_max')} {
    padding: ${tokPx('primitives_space_48')} ${tokPx('primitives_space_12')};
  }
`

const BackgroundImageContainer = styled.div`
  position: absolute;
  inset: 0;
`

const HeroContentContainer = styled.div` 
  position: relative;
  background-color: #fff;
  margin: 0 auto;
  width: fit-content;
  max-width: ${tokPx('primitives_area_size_720')};
  min-width: ${tokPx('primitives_area_size_320')};
  display: flex;
  align-items: center;
  flex-direction: column;
  text-align: center;
  border-radius: ${tokPx('primitives_corner_radius_xl')};
  color: ${tok('semantics_content_color')};
  padding: ${tokPx('primitives_space_24')};

  @media ${mqMax('primitives_breakpoint_s_max')} {
    padding: 0;
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    padding: ${tokPx('primitives_space_24')} ${tokPx('primitives_space_64')};
  }
`

const HeroTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;

  @media ${mqMax('primitives_breakpoint_s_max')} {
    padding: ${tokPx('primitives_space_16')};
  }
`

const HeroTitle = styled(Typography)`
  ${Typography.modVariant('display-e-regular')}

  @media ${mqMax('primitives_breakpoint_s_max')} {
    ${Typography.modVariant('display-g-regular')}
  }
`

const HeroBookingNumber = styled(Tag)`
  margin-bottom: ${tokPx('primitives_space_8')};
`

const HeroTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${tokPx('primitives_space_4')};
  justify-content: center;
  margin-top: ${tokPx('primitives_space_8')};

  &:empty {
    margin-top: 0;
  }
`

const HeroTabBar = styled(TabBar)`
  margin-top: 28px;
  background-color: ${tok('semantics_views_tinted_background_color')};

  @media ${mqMax('primitives_breakpoint_s_max')} {
    margin-top: 0px;
    border-radius: 0px 0px ${tokPx('primitives_corner_radius_xl')} ${tokPx('primitives_corner_radius_xl')};
    border-width: 0px;
    border-top-width: 2px;
  }
`

interface HeroProps {
  title: string
  bookingNumber?: string
  location?: string
  period?: string
  tags?: React.ReactElement
  backgroundImageSrc?: string
  backgroundImageAlt?: string
  activeTab?: Tabs
  onTabChange?: (value: Tabs) => void
}

export function Hero({
  title,
  bookingNumber,
  location,
  period,
  tags,
  backgroundImageSrc,
  backgroundImageAlt,
  activeTab,
  onTabChange,
}: HeroProps) {
  const intl = useIntl()
  const normalizedBackgroundImageSrc = (backgroundImageSrc || '').split('?')[0]
  const imageOrigin = getImageOrigin(backgroundImageSrc || '')
  const optimizedBackgroundImageSrc = normalizedBackgroundImageSrc
    ? buildImgSrc(
        normalizedBackgroundImageSrc,
        HERO_IMAGE_SRCSET_WIDTHS[0],
        undefined,
        imageOrigin,
      )
    : ''
  const optimizedBackgroundImageSrcSet = normalizedBackgroundImageSrc
    ? buildImgSrcSet(
        normalizedBackgroundImageSrc,
        HERO_IMAGE_SRCSET_WIDTHS,
        imageOrigin,
      )
    : undefined
  const placeholderSrc = normalizedBackgroundImageSrc
    ? buildImgSrc(
        normalizedBackgroundImageSrc,
        HERO_IMAGE_PLACEHOLDER_WIDTH,
        undefined,
        imageOrigin,
      )
    : ''

  const tabs: TabComponent[] = [
    { value: 'booking', icon: 'Suitcase', label: 'group_responsible_tab_booking' },
    { value: 'participants', icon: 'ThreePeople', label: 'common_participants' },
    { value: 'rooms', icon: 'Bed', label: 'group_responsible_tab_rooms' },
    { value: 'bus', icon: 'BusFront', label: 'group_responsible_tab_bus' },
  ]

  return (
    <HeroContainer>
      {optimizedBackgroundImageSrc && (
        <BackgroundImageContainer>
          <LazyImage
            src={optimizedBackgroundImageSrc}
            srcSet={optimizedBackgroundImageSrcSet}
            sizes={HERO_IMAGE_SIZES}
            placeholderSrc={placeholderSrc}
            alt={backgroundImageAlt || title}
          />
        </BackgroundImageContainer>
      )}
      <HeroContentContainer>
        <HeroTextContainer>
          {bookingNumber && (
            <HeroBookingNumber variant="informative-tinted">
              {intl.formatMessage({ id: 'common_booking_number_label' }, { bookingNumber })}
            </HeroBookingNumber>
          )}
          <HeroTitle>{title}</HeroTitle>
          {location && <Typography>{location}</Typography>}
          {period && <Typography>{period}</Typography>}
          {tags && <HeroTagsContainer>{tags}</HeroTagsContainer>}
        </HeroTextContainer>
        {tabs && activeTab && onTabChange && (
          <HeroTabBar defaultActive={activeTab} onActiveChange={({ value }) => onTabChange(value as Tabs)}>
            {tabs.map(tab => (
              <TabBar.Tab key={tab.value} value={tab.value}>
                <Icon glyph={tab.icon} />
                {intl.formatMessage({ id: tab.label })}
              </TabBar.Tab>
            ))}
          </HeroTabBar>
        )}
      </HeroContentContainer>
    </HeroContainer>
  )
}
